import { useCallback } from 'react'
import type { Control, UseFormSetValue } from 'react-hook-form'
import { useFormState } from 'react-hook-form'
import { useWindowDimensions, View } from 'react-native'
import { Card, Text } from 'react-native-paper'
import { BREAKPOINTS } from '@/lib/config'
import { JOB_STATUS_ORDER, type JobFormData, JobStatus } from '@/types/jobs'
import { AddressEdit } from '@/ui/components/AddressEdit'
import { SelectInput, TextInput } from '@/ui/form'
import { CustomerSelect, type CustomerWithAddress } from '@/ui/jobs/CustomerSelect'
import { TagSelect } from '@/ui/jobs/TagSelect'

interface JobFormFieldsProps {
    control: Control<JobFormData>
    setValue?: UseFormSetValue<JobFormData>
}

// Form selector uses shorter, edit-oriented labels than getJobStatusDisplay
// (e.g. 'Active' vs 'In Progress'); order follows the shared JOB_STATUS_ORDER.
const STATUS_LABELS: Record<JobStatus, string> = {
    [JobStatus.PENDING]: 'Pending',
    [JobStatus.ACTIVE]: 'Active',
    [JobStatus.PAUSED]: 'Paused',
    [JobStatus.COMPLETED]: 'Completed',
    [JobStatus.CANCELED]: 'Canceled',
}

const statusOptions = JOB_STATUS_ORDER.map(status => ({
    label: STATUS_LABELS[status],
    value: status,
}))

export function JobFormFields({ control, setValue }: JobFormFieldsProps) {
    const { width } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg
    const { errors } = useFormState({ control })

    const handleCustomerChange = useCallback(
        (customer: CustomerWithAddress) => {
            if (!setValue || !customer.address) return
            setValue('expand.address.line1', customer.address.line1 || '')
            setValue('expand.address.line2', customer.address.line2 || '')
            setValue('expand.address.city', customer.address.city || '')
            setValue(
                'expand.address.stateProvinceCounty',
                customer.address.stateProvinceCounty || ''
            )
            setValue('expand.address.zipOrPostcode', customer.address.zipOrPostcode || '')
            setValue('expand.address.country', customer.address.country || '')
        },
        [setValue]
    )
    const customerError = errors.customer as
        | { message?: string; identifier?: { message?: string } }
        | undefined
    const rawCustomerErrorMessage =
        (typeof customerError?.identifier === 'object' && customerError?.identifier
            ? customerError.identifier?.message
            : undefined) || customerError?.message
    const customerErrorMessage = rawCustomerErrorMessage ? 'Customer is required' : undefined
    const tagError = errors.tags as { message?: string } | undefined
    const tagErrorMessage = tagError?.message

    return (
        <>
            <Card mode="outlined" contentStyle={{ padding: 16 }}>
                <View style={{ flexDirection: 'column' }}>
                    <Text variant="headlineMedium">Job Information</Text>

                    <View
                        style={{
                            flexDirection: isDesktop ? 'row' : 'column',
                            gap: isDesktop ? 16 : 0,
                        }}
                    >
                        <View style={{ flex: isDesktop ? 1 : undefined }}>
                            <TextInput
                                control={control}
                                name="name"
                                label="Job Name *"
                                placeholder="Enter job name"
                                autoFocus
                            />
                        </View>

                        <View style={{ width: isDesktop ? 200 : undefined }}>
                            <TextInput
                                control={control}
                                name="identifier"
                                label="Identifier *"
                                placeholder="job-identifier"
                            />
                        </View>
                    </View>

                    <SelectInput
                        control={control}
                        name="status"
                        label=""
                        options={statusOptions}
                        horizontal={true}
                    />

                    <CustomerSelect
                        control={control}
                        name="customer"
                        label="Customer"
                        required={true}
                        errorMessage={customerErrorMessage}
                        onCustomerChange={handleCustomerChange}
                    />

                    <TagSelect
                        control={control}
                        name="tags"
                        label="Tags"
                        errorMessage={tagErrorMessage}
                    />

                    <TextInput
                        control={control}
                        name="notes"
                        label="Notes"
                        placeholder="Enter job notes (optional)"
                        multiline
                        numberOfLines={3}
                    />
                </View>
            </Card>

            <Card mode="outlined" contentStyle={{ padding: 16 }}>
                <View style={{ flexDirection: 'column' }}>
                    <Text variant="headlineMedium">Job Address</Text>
                    <AddressEdit<JobFormData> control={control} showTitle={false} />
                </View>
            </Card>
        </>
    )
}
